import React, { useState, useEffect } from 'react';
import { B } from '../constants';
import { borderRadius, transitions } from '../designTokens';
import { blogs as blogsAPI } from '../api';

const STATUSES = ['all', 'published', 'draft'];

const isPublished = b => b.isPublished || b.status === 'published';

const DEST_IMAGES = {
  'hyderabad': 'photo-1599661046289-e31897846e41',
  'hyd': 'photo-1599661046289-e31897846e41',
  'rishikesh': 'photo-1544367567-0f2fcb009e0b',
  'bali': 'photo-1537996194471-e657df975ab4',
  'tokyo': 'photo-1540959733332-eab4deabeeaf',
  'santorini': 'photo-1613395877344-13d4a8e0d49e',
  'machu picchu': 'photo-1587595431973-160d0d94add1',
  'paris': 'photo-1502602898657-3e91760cbb34',
  'safari': 'photo-1516426122078-c23e76319801',
  'maldives': 'photo-1514282401047-d79a71a590e8',
  'swiss alps': 'photo-1531366936337-7c912a4589a7',
  'bangkok': 'photo-1508009603885-50cf7c579365',
  'ibiza': 'photo-1539037116277-4db20889f2d4',
  'dubai': 'photo-1512453979798-5ea266f8880c',
  'utah': 'photo-1506905925346-21bda4d32df4',
  'zion': 'photo-1506905925346-21bda4d32df4',
  'india': 'photo-1544367567-0f2fcb009e0b',
  'indonesia': 'photo-1537996194471-e657df975ab4',
  'japan': 'photo-1540959733332-eab4deabeeaf',
  'greece': 'photo-1613395877344-13d4a8e0d49e',
  'peru': 'photo-1587595431973-160d0d94add1',
  'france': 'photo-1502602898657-3e91760cbb34',
  'kenya': 'photo-1516426122078-c23e76319801',
  'switzerland': 'photo-1531366936337-7c912a4589a7',
  'thailand': 'photo-1508009603885-50cf7c579365',
  'spain': 'photo-1539037116277-4db20889f2d4',
  'uae': 'photo-1512453979798-5ea266f8880c',
};

const GENERIC_TRAVEL = 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&q=80';

function blogImage(blog) {
  if (blog.coverImage) return blog.coverImage;
  if (blog.cover) return blog.cover;
  if (blog.trip?.coverImage && /^https?:\/\//i.test(blog.trip.coverImage)) return blog.trip.coverImage;
  const dest = (blog.trip?.destination || '').toLowerCase();
  for (const [key, photoId] of Object.entries(DEST_IMAGES)) {
    if (dest.includes(key)) return `https://images.unsplash.com/${photoId}?w=600&q=80`;
  }
  return GENERIC_TRAVEL;
}

export default function MockBlogsScreen({ goScreen, user, onAuthRequired }) {
  const [filter, setFilter] = useState('all');
  const [blogList, setBlogList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    blogsAPI.list()
      .then(setBlogList)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user]);

  const filtered = filter === 'all' ? blogList : filter === 'published' ? blogList.filter(isPublished) : blogList.filter(b => !isPublished(b));

  if (!user) {
    return (
      <div style={{ background: B.sandPattern, minHeight: '100vh', paddingBottom: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: '70px 20px', color: B.muted }}>
          <div style={{ fontSize: 46, marginBottom: 12 }}>🔒</div>
          <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: B.night }}>Sign in to see your blogs</h3>
          <p style={{ marginTop: 8, fontSize: 14 }}>Your travel stories will appear here.</p>
          <button onClick={onAuthRequired}
            style={{ marginTop: 20, background: B.forest, color: 'white', border: 'none', padding: '12px 28px', borderRadius: borderRadius.pill, fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: B.sandPattern, minHeight: '100vh', paddingBottom: 80 }}>
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '40px clamp(16px, 3vw, 40px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 20, marginBottom: 32 }}>
          <div>
            <span style={{ fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 700, color: B.forest, fontFamily: "'Montserrat',sans-serif" }}>Your stories</span>
            <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: 800, marginTop: 6 }}>Blogs</h1>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 30, flexWrap: 'wrap' }}>
          {STATUSES.map(s => (
            <button key={s} onClick={() => setFilter(s)}
              style={{
                padding: '9px 16px', borderRadius: borderRadius.pill,
                border: `1.5px solid ${filter === s ? B.forest : '#e7e2d9'}`,
                background: filter === s ? B.forest : 'white',
                color: filter === s ? 'white' : B.night,
                cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                fontSize: 13, fontWeight: 600, textTransform: 'capitalize',
                transition: `all ${transitions.fast}`,
              }}>
              {s === 'all' ? '🌍 All' : s === 'published' ? '📝 Published' : '📄 Draft'}
            </button>
          ))}
        </div>

        {loading ? Array.from({ length: 4 }).map((_, i) => (
          <div key={i} style={{ background: 'white', borderRadius: borderRadius.lg, overflow: 'hidden', boxShadow: '0 2px 8px rgba(15,25,35,0.06)', marginBottom: 16 }}>
            <div style={{ height: 180, background: 'rgba(15,25,35,0.06)', animation: 'pulse 1.5s ease-in-out infinite' }} />
            <div style={{ padding: 18 }}>
              <div style={{ height: 18, width: '70%', background: 'rgba(15,25,35,0.06)', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite', marginBottom: 10 }} />
              <div style={{ height: 14, width: '100%', background: 'rgba(15,25,35,0.06)', borderRadius: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />
            </div>
          </div>
        )) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 24 }}>
            {filtered.map(blog => (
              <div key={blog.id} onClick={() => goScreen('blogView', blog)}
                style={{ background: 'white', borderRadius: borderRadius.lg, overflow: 'hidden', cursor: 'pointer', boxShadow: '0 2px 8px rgba(15,25,35,0.06)', transition: `all ${transitions.slow}` }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(15,25,35,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(15,25,35,0.06)'; }}>
                <div style={{ height: 180, background: `url(${blogImage(blog)}) center/cover` }}>
                  <div style={{ padding: 12, display: 'flex', justifyContent: 'flex-end' }}>
                    <span style={{
                      padding: '4px 12px', borderRadius: borderRadius.pill, fontSize: 11, fontWeight: 700,
                      background: isPublished(blog) ? B.mint : B.amber,
                      color: isPublished(blog) ? B.night : 'white',
                    }}>
                      {isPublished(blog) ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>
                <div style={{ padding: 18 }}>
                  <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{blog.title}</h3>
                  <p style={{ color: B.muted, fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>{blog.excerpt || blog.description || ''}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: B.muted, borderTop: '1px solid #e7e2d9', paddingTop: 10 }}>
                    <span>📅 {blog.updatedAt ? new Date(blog.updatedAt).toLocaleDateString() : blog.date || ''}</span>
                    <span>❤️ {blog.likes || blog.likeCount || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '70px 20px', color: B.muted }}>
            <div style={{ fontSize: 46, marginBottom: 12 }}>📝</div>
            <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22 }}>No blogs yet</h3>
            <p style={{ marginTop: 8, fontSize: 14 }}>Start writing your travel stories!</p>
          </div>
        )}
      </div>
    </div>
  );
}
